import { useTranslation } from 'next-i18next';
import { memo, ReactElement, useCallback, useMemo } from 'react';
import { ISupplierDetailVenue } from '@bridebook/models/source/models/Suppliers.types';
import { slugify } from '@bridebook/toolbox';
import type { Market } from '@bridebook/toolbox/src/gazetteer';
import { IPackagesSections } from '@bridebook/toolbox/src/map-packages-to-sections';
import { Box, TColor } from '@bridebook/ui';
import {
  IconAccommodation,
  IconExclusiveUse,
  IconGuest,
  IconWeddingLicence,
} from '@bridebook/ui/src/icons';
import { PricingCategory } from '@bridebook/web/app-shared/lib/supplier/types';
import PricingIcon from '@bridebook/web/components/generic/pricing-icon/pricing-icon';
import {
  getPricingValueString,
  isSupplierWithPricingDetails,
} from '@bridebook/web/lib/supplier/utils';
import { CastedDiningCapacity } from '@bridebook/web/lib/types';
import InfoBlock from '@bridebook/web/app-shared/components/supplier/sections/supplier-key-info/info-block';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/supplier-key-info/supplier-key-info.style';

interface IProps {
  accomodationRooms: number;
  ceremonyReception: NonNullable<ISupplierDetailVenue['ceremonyReception']>;
  currencyCode: string;
  diningCapacity: CastedDiningCapacity;
  hideKeyInfoSections?: KeyInfoSections[];
  market: Market;
  packages: IPackagesSections | undefined;
  showFeaturesSection: () => void;
  showPricingSection: () => void;
  smallIcons: boolean;
  supplierPricingCategory: PricingCategory | undefined;
  venueDetails: NonNullable<ISupplierDetailVenue['details']>;
}

export type KeyInfoSections =
  | 'price'
  | 'seats'
  | 'accomodation'
  | 'exclusiveUse'
  | 'weddingLicense';

type KeyInfoItem = {
  name: KeyInfoSections;
  icon: ReactElement;
  text: string;
  disabled?: boolean;
  onClick: { (): void };
};

const SupplierKeyInfo = ({
  accomodationRooms,
  ceremonyReception,
  currencyCode,
  diningCapacity,
  hideKeyInfoSections = [],
  market,
  packages,
  showFeaturesSection,
  showPricingSection,
  smallIcons,
  supplierPricingCategory,
  venueDetails,
}: IProps) => {
  const { t } = useTranslation('supplier');
  const exclusiveUse = venueDetails.includes('exclusiveUse');
  const weddingLicence = ceremonyReception.includes('weddingLicence');

  const iconColor = useCallback(
    (disabled: boolean): TColor => (disabled ? 'space15' : 'indigoCrush'),
    [],
  );

  const hasPricingDetails = isSupplierWithPricingDetails(packages);

  const prices = getPricingValueString(
    currencyCode,
    market,
    packages,
    supplierPricingCategory,
    false,
  );
  const noSeated = !diningCapacity;

  const budgetItem: KeyInfoItem = {
    name: 'price',
    icon: (
      <PricingIcon
        pricingCategory={!hasPricingDetails ? supplierPricingCategory : undefined}
        iconProps={{ width: smallIcons ? 22 : 36, borderColor: 'indigoCrush' }}
      />
    ),
    text: prices,
    onClick: showPricingSection,
  };

  const items: KeyInfoItem[] = useMemo(
    () => [
      {
        name: 'seats',
        icon: <IconGuest width={smallIcons ? 16 : 19} color={iconColor(noSeated)} />,
        text: diningCapacity
          ? t('supplierKeyInfo.seats', { dining: diningCapacity })
          : t('supplierKeyInfo.noSeats'),
        disabled: noSeated,
        onClick: showFeaturesSection,
      },
      {
        name: 'accomodation',
        icon: (
          <IconAccommodation
            width={smallIcons ? 22 : 31}
            color={iconColor(accomodationRooms === 0)}
          />
        ),
        text:
          accomodationRooms === 0
            ? t('supplierKeyInfo.noBeds')
            : t('supplierKeyInfo.beds', {
                rooms: accomodationRooms > 0 ? accomodationRooms : '',
              }),
        disabled: accomodationRooms === 0,
        onClick: showFeaturesSection,
      },
      {
        name: 'exclusiveUse',
        icon: <IconExclusiveUse width={smallIcons ? 19 : 24} color={iconColor(!exclusiveUse)} />,
        text: t('supplierKeyInfo,exclusiveUse'),
        disabled: !exclusiveUse,
        onClick: showFeaturesSection,
      },
      {
        name: 'weddingLicense',
        icon: (
          <IconWeddingLicence width={smallIcons ? 19 : 22} color={iconColor(!weddingLicence)} />
        ),
        text: t('supplierKeyInfo.licence'),
        disabled: !weddingLicence,
        onClick: showFeaturesSection,
      },
    ],
    [
      accomodationRooms,
      diningCapacity,
      exclusiveUse,
      iconColor,
      noSeated,
      showFeaturesSection,
      smallIcons,
      t,
      weddingLicence,
    ],
  );

  const styles = componentStyles();

  const InfoBlockItems = useMemo(
    () =>
      items.map((item) => {
        if (hideKeyInfoSections.includes(item.name)) {
          return;
        }

        return (
          <Box style={styles.itemWrap} key={`supplier-key-info-item-${slugify(item.text)}`}>
            <InfoBlock
              icon={item.icon}
              text={item.text}
              disabled={item.disabled || null}
              onClick={item.onClick}
            />
          </Box>
        );
      }),
    [hideKeyInfoSections, items, styles.itemWrap],
  );

  return (
    <Box style={styles.wrapper}>
      <Box style={styles.leftBlock}>
        <InfoBlock
          icon={budgetItem.icon}
          text={budgetItem.text}
          onClick={budgetItem.onClick}
          large
        />
      </Box>
      <Box style={styles.rightBlock}>{InfoBlockItems}</Box>
    </Box>
  );
};

export default memo(SupplierKeyInfo);
